import React from "react";
import { useAppSelector } from "../../../../store/hooks";
import { selectBreakawaySections, selectTotalTonnage } from "../../../../store/selectors/breakawayHullSelectors";
import { formatCurrency, formatNumber } from "../../../../utils/formatting";

/**
 * Summary component for breakaway hull power plant configuration
 * Shows power distribution across all sections
 */
export const BreakawayPowerPlantSummary: React.FC = () => {
  const sections = useAppSelector(selectBreakawaySections);
  const totalTonnage = useAppSelector(selectTotalTonnage);

  // Calculate totals
  const totals = React.useMemo(() => {
    let totalPower = 0;
    let totalCost = 0;
    let totalMaintenance = 0;
    let totalWeight = 0;
    let configuredSections = 0;

    sections.forEach(section => {
      if (section.powerPlant) {
        totalPower += Math.round(section.powerPlant.power);
        totalCost += section.powerPlant.cost;
        totalMaintenance += section.powerPlant.maintenanceCost;
        totalWeight += Math.round(section.powerPlant.weight);
        configuredSections++;
      }
    });

    return {
      totalPower,
      totalCost,
      totalMaintenance,
      totalWeight,
      configuredSections,
      totalSections: sections.length,
    };
  }, [sections]);

  if (sections.length === 0) {
    return null;
  }

  return (
    <div className="bg-gray-800 text-white p-4 shadow-lg">
      <div className="container mx-auto grid grid-cols-[1fr_auto_auto] gap-x-12 items-center">
        {/* Details Row */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <div>
            <span className="text-gray-400 block">Power Plant Status</span>
            <span className={`font-semibold ${
              totals.configuredSections === totals.totalSections 
                ? 'text-green-400' 
                : 'text-orange-400'
            }`}>
              {totals.configuredSections === totals.totalSections 
                ? 'Complete' 
                : `${totals.configuredSections}/${totals.totalSections} Sections`
              }
            </span>
          </div>
          
          {totals.configuredSections > 0 && (
            <>
              <div className="text-gray-400">|</div>
              <div>
                <span className="text-gray-400 block">Total Cost</span>
                <span className="font-semibold text-green-400">
                  {formatCurrency(totals.totalCost)}
                </span>
              </div>
              <div className="text-gray-400">|</div>
              <div>
                <span className="text-gray-400 block">Monthly Maintenance</span>
                <span className="font-semibold text-purple-300">
                  {formatCurrency(totals.totalMaintenance)}
                </span>
              </div>
              <div className="text-gray-400">|</div>
              <div>
                <span className="text-gray-400 block">Sections</span>
                <span className="font-semibold">
                  {sections.length} breakaway • {formatNumber(totalTonnage)} tons
                </span>
              </div>
            </>
          )}
        </div>

        {/* Total Power */}
        <div className="text-right">
          <span className="text-gray-400 block text-sm">Total Power</span>
          <span className="text-xl font-bold text-blue-400">
            {formatNumber(totals.totalPower)} Power
          </span>
        </div>

        {/* Section Power Distribution */}
        <div className="flex flex-col items-center justify-center border-l border-r border-gray-700 px-8">
          <span className="text-gray-400 block text-sm">Section Power</span>
          <div className="text-xs space-y-1">
            {sections.map(section => (
              <div key={section.id} className="text-center">
                <span className="text-gray-400">{section.name}: </span>
                <span className={`font-medium ${
                  section.powerPlant 
                    ? 'text-blue-400' 
                    : 'text-gray-500'
                }`}>
                  {section.powerPlant 
                    ? `${formatNumber(Math.round(section.powerPlant.power))} Power`
                    : 'Not configured'
                  }
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
